<template>
  <v-menu
    offset-y
    left
    nudge-bottom="22"
    :elevation="$vuetify.theme.dark ? 9 : 8"
    content-class="list-style notification-menu-content"
    :close-on-content-click="false"
    min-width="400"
    max-width="500"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn class="me-2 no-bg" icon :aria-label="$t('common.notifications')" v-bind="attrs" v-on="on">
        <v-badge
          :value="notifications.filter(notification => !notification.is_read).length > 0"
          dot
          color="error"
        >
          <v-icon> {{ icons.mdiBellOutline }} </v-icon>
        </v-badge>
      </v-btn>
    </template>
    <v-card class="app-bar-notification-content-container">
      <perfect-scrollbar
        class="ps-user-notifications"
        :options="perfectScrollbarOptions"
      >
        <v-list class="py-0">
          <!-- Header -->
          <v-list-item
            class="d-flex"
            link
            @click="clearAllNotifications"
          >
            <div class="d-flex align-center justify-space-between flex-grow-1">
              <span class="font-weight-semibold"> {{ $t('common.notifications') }}</span>

              <div>
                <v-btn
                  small
                  text
                  class="mr-2"
                  @click="clearAllNotifications"
                  v-if="notifications.some(notification => !notification.is_read)"
                >
                  <span class="mr-2"> {{ $t('common.clear_all') }}</span>
                  <v-icon>{{ icons.mdiCheckAll }}</v-icon>
                </v-btn>
                <v-chip
                  class="v-chip-light-bg primary--text font-weight-semibold"
                  small
                >
                  {{ notifications.filter(notification => !notification.is_read).length }} {{ $t('common.unread') }}
                </v-chip>
              </div>
            </div>
          </v-list-item>
          <v-divider></v-divider>

          <!-- Notifications -->
          <template v-if="notifications.length > 0">
            <div v-for="(notification, index) in notifications" :key="index">
              <v-list-item
                :key="notification.title"
                link
                :style="{ 'opacity': !notification.is_read ? '1' : '0.6' }"
                @click="handleNotificationClick(notification)"
              >
                <!-- Content -->
                <v-list-item-content class="d-block">
                  <v-chip
                    x-small
                    label
                    :color="notification.related_object_type === 'actionplan' ? 'secondary' : 'primary'"
                    text-color="white"
                    class="mb-1"
                  >
                    {{ notification.related_object_type === 'actionplan'
                      ? $t('common.action_plan')
                      : notification.related_object_type === 'school'
                        ? $t('common.report')
                        : $t('common.evaluation') }}
                  </v-chip>
                  <v-list-item-title class="text-sm font-weight-semibold">
                    {{ notification.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-text="notification.message" class="text-sm">
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="notification.school_name" class="text-xs">
                    <v-icon color="primary" small>
                      {{ icons.mdiSchoolOutline }}
                    </v-icon>
                    {{ notification.school_name }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="notification.sender" class="text-xs">
                    <v-icon color="primary"small>
                      {{ icons.mdiAccount}}
                    </v-icon>
                    {{ notification.sender.name }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <!-- Item Action/Time -->
                <v-list-item-action>
                  <v-list-item-action-text v-text="notification.created_at.split('T')[0]"></v-list-item-action-text>
                  <v-icon color="primary" v-if="!notification.is_read">
                    {{ icons.mdiCircleMedium }}
                  </v-icon>
                  <v-icon color="success" v-else>
                    {{ icons.mdiCheckAll }}
                  </v-icon>
                  <!-- <span class="text--secondary text-xs">{{ notification.created_at.split('T')[0] }}</span> -->
                </v-list-item-action>
              </v-list-item>
              <v-divider :key="index"></v-divider>
            </div>
          </template>
          <template v-else>
            <v-list-item>
              <v-list-item-title class="text-center font-italic">{{ $t('auth_users.no_notifications') }}</v-list-item-title>
              <v-divider></v-divider>
            </v-list-item>
          </template>
          <v-list-item
            link
            key="read-all-btn"
            class="read-all-btn-list-item"
          >
            <v-btn
              block
              color="primary"
              @click="addMoreNotifications"
              :disabled="!nextPage"
            >
              {{ $t('auth_users.read_more') }}
            </v-btn>
          </v-list-item>
        </v-list>
      </perfect-scrollbar>
    </v-card>
  </v-menu>
</template>

<script>
import { mdiBellOutline, mdiCircleMedium, mdiCheckAll, mdiAccount, mdiDelete, mdiSchoolOutline } from '@mdi/js'
import { getInitialName } from '@core/utils'
import { ref, onMounted, onUnmounted } from '@vue/composition-api'
import axiosIns from '@axios'
import { useRouter } from '@core/utils'
// 3rd Party
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import textFormatMixin from '@/utils/textFormatMixin'

export default {
  components: {
    // 3rd Party
    PerfectScrollbar,
  },
  mixins: [textFormatMixin],
  setup() {
    const { router } = useRouter()
    const page = ref(1)
    const nextPage = ref(null)
    const pageSize = ref(5)
    const notifications = ref([])
    let notificationInterval = null

    const perfectScrollbarOptions = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const getNotifications = async () => {
      const form = {
        page: page.value,
        page_size: pageSize.value,
      }
      try {
        const response = await axiosIns.getNotifications(form)
        notifications.value = response.results
        nextPage.value = response.next?.length > 0 ? page.value + 1 : null
      } catch {
        // Error already handled by axios interceptor (e.g. token expiration → redirect to login)
      }
    }
    // Start periodic notification fetching
    const startNotificationPolling = () => {
      // Clear any existing interval
      if (notificationInterval) {
        clearInterval(notificationInterval)
      }

      // Set up new interval for every 5 minutes (300000 ms)
      notificationInterval = setInterval(() => {
        getNotifications()
      }, 300000) // 5 minutes
    }

    // Stop periodic notification fetching
    const stopNotificationPolling = () => {
      if (notificationInterval) {
        clearInterval(notificationInterval)
        notificationInterval = null
      }
    }

    const addMoreNotifications = async () => {
      const form = {
        page: nextPage.value,
        page_size: pageSize.value,
      }
      try {
        const response = await axiosIns.getNotifications(form)
        notifications.value = [...notifications.value, ...response.results]
        page.value += 1
        nextPage.value = response.next?.length > 0 ? page.value + 1 : null
      } catch (error) {
        console.log(error)
      }
    }

    const handleNotificationClick = async notification => {
      // Mark notification as read
      if (
        notification.related_object_type === "evaluation"
        || notification.related_object_type === "actionplan"
        || notification.related_object_type === "school"
      ) {
        let targetRoute
        if (notification.related_object_type === "actionplan") {
          targetRoute = `/evaluations/${notification.evaluation_id}/action-plan`
        } else if (notification.related_object_type === "school") {
          targetRoute = '/reports'
        } else {
          targetRoute = `/evaluations/detail/${notification.object_id}/instrument/${1}`
        }
        await axiosIns.markNotificationAsRead(notification.id)
        Object.assign(notification, { is_read: true })
        // Check if we're already on the target route to avoid NavigationDuplicated error
        if (router.currentRoute.path !== targetRoute) {
          router.push(targetRoute).catch(err => {
            // Handle navigation errors gracefully
            if (err.name !== 'NavigationDuplicated') {
              console.error('Navigation error:', err)
            }
          })
        } else {
          // If already on the target route, you can perform another action
          console.log('Already on the target evaluation page')
        }
      }
    }

    const clearAllNotifications = async () => {
      try {
        await axiosIns.readAllNotifications()
        notifications.value = notifications.value.map(notification => Object.assign(notification, { is_read: true }))
      } catch (error) {
        console.log(error)
      }
    }

    onMounted(() => {
      // Initialize notifications and start polling
      getNotifications()
      startNotificationPolling()
    })

    onUnmounted(() => {
      // Clean up interval when component is unmounted
      stopNotificationPolling()
    })

    return {
      notifications,
      page,
      nextPage,
      pageSize,
      getInitialName,
      perfectScrollbarOptions,
      addMoreNotifications,
      handleNotificationClick,
      clearAllNotifications,
      icons: {
        mdiBellOutline,
        mdiCircleMedium,
        mdiCheckAll,
        mdiAccount,
        mdiDelete,
        mdiSchoolOutline,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~vuetify/src/styles/styles.sass';

.app-bar-notification-content-container {
  .read-all-btn-list-item {
    padding-top: 14px;
    padding-bottom: 14px;
    min-height: unset;
  }
}

.ps-user-notifications {
  max-height: calc(var(--vh, 1vh) * 85);
}

.notification-menu-content {
  @media #{map-get($display-breakpoints, 'xs-only')} {
    min-width: calc(100vw - (1.5rem * 2)) !important;
    left: 50% !important;
    transform: translateX(-50%);
  }
}
</style>
